import Input from '@/components/ui/forms/input';
import { ptBrCommon } from '@/lib/pt-br/messages';
import PasswordInput from '@/components/ui/forms/password-input';
import Button from '@/components/ui/button';
import { useModalAction } from '@/components/ui/modal/modal.context';
import { Form } from '@/components/ui/forms/form';
import type { RegisterUserInput } from '@/types';
import * as yup from 'yup';
import { useRegister } from '@/framework/user';
import { gtmSignUp } from '@/lib/gtm';
import Radio from '../ui/forms/radio/radio';
import Checkbox from '../ui/forms/checkbox/checkbox';
import { PasswordRules } from './password-rules';
import { cpfMask, dateMask, formatDate } from '@/lib/utils/mask';
import {
  cpfValidator,
  dateOfBirthValidator,
  removeMask,
} from '@/lib/utils/validation';
import PhoneInput from '../ui/forms/phone-input';
import { PlusIcon } from '../icons/plus-icon';
import { MinusIcon } from '../icons/minus-icon';
import { CloseIcon } from '../icons/close-icon';
import { atom, useAtom } from 'jotai';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import {
  containsLower,
  containsUpper,
  testPassword,
} from '@/lib/utils/password';
import { clientLogger } from '@/lib/client-logger';
import TurnstileWidget from '@/components/parceiros-de-entrega/turnstile-widget';
import { toast } from 'react-toastify';

const TURNSTILE_ENABLED = Boolean(process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY);
const TURNSTILE_ACTION = 'signup';

const registerFormSchema = yup.object().shape({
  first_name: yup.string().required('error-name-required'),
  last_name: yup.string().required('error-name-required'),
  cellphone: yup
    .string()
    .required('O celular é obrigatório')
    .test(
      'valid-phone',
      'Por favor, preencha o celular completo',
      (value) => !!value && value.length >= 12 && value !== '55',
    ),
  phone: yup.string(),
  social_name: yup.string(),
  cpf: yup
    .string()
    .required('error-cpf-required')
    .test('cpf', 'CPF inválido', (cpf) => cpfValidator(cpf)),
  email: yup
    .string()
    .email('error-email-format')
    .required('error-email-required'),
  password: yup
    .string()
    .required('error-password-required')
    .test(
      'min-length',
      'A senha deve ter no mínimo 8 caracteres',
      (value) => !!value && value.length >= 8,
    )
    .test(
      'has-number',
      'A senha deve conter ao menos 1 número',
      (value) => !!value && testPassword(value),
    )
    .test(
      'has-uppercase',
      'A senha deve conter ao menos 1 letra maiúscula',
      (value) => !!value && containsUpper(value),
    )
    .test(
      'has-lowercase',
      'A senha deve conter ao menos 1 letra minúscula',
      (value) => !!value && containsLower(value),
    ),
  repeat_password: yup
    .string()
    .required('error-password-required')
    .oneOf([yup.ref('password')], 'Ambas as senhas precisam ser iguais'),
  birth_date: yup
    .string()
    .test(
      '(^0[1-9]|[12][0-9]|3[01])/(0[1-9]|1[0-2])/(d{4}$)',
      'Data inválida',
      (dateOfBirth) => dateOfBirthValidator(dateOfBirth!),
    )
    .required(),
  receive_email: yup.bool(),
  accepted_terms: yup
    .bool()
    .required()
    .oneOf([true], 'Você deve aceitar os termos'),
  gender: yup.string().required('Por favor, selecione seu gênero'),
});

const isInputVisibleAtom = atom(false);

function RegisterForm() {
const { openModal, closeModal } = useModalAction();
  const { mutate, isLoading, formError, setFormError } = useRegister();
  const router = useRouter();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);

  const [isInputVisible, setIsInputVisible] = useAtom(isInputVisibleAtom);

  const toggleInput = () => {
    setIsInputVisible((prev) => !prev);
  };

  const missingChallenge = TURNSTILE_ENABLED && !turnstileToken;

  function onSubmit(values: RegisterUserInput) {
    if (missingChallenge) {
      toast.error('Complete o desafio de segurança para continuar.');
      return;
    }

    const referralCode = router.query.referral_code as string;
    const decodedReferralCode = referralCode
      ? decodeURIComponent(referralCode).replace(/=$/, '')
      : null;

    // Send GTM sign_up event
    gtmSignUp();

    const payload: Record<string, unknown> = {
      first_name: values.first_name,
      last_name: values.last_name,
      cellphone: values.cellphone,
      phone: values.phone,
      social_name: values.social_name,
      cpf: removeMask(values.cpf),
      email: values.email,
      password: values.password,
      birth_date: formatDate(values.birth_date),
      gender: values.gender,
      receive_email: values.receive_email,
      ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
    };

    if (decodedReferralCode) {
      payload.invited_by_referral_code = decodedReferralCode;
    }

    mutate(payload as any, {
      onError: (error: any) => {
        setTurnstileToken(null);
        setTurnstileKey((key) => key + 1);
        clientLogger.error('Register: registration failed', {
          email: payload.email,
          cpf: payload.cpf,
          responseData: error?.response?.data,
          status: error?.response?.status,
        });
      },
    });
  }

  const handleCPFChange = (event: any) => {
    const maskedValue = cpfMask(event.target.value);
    event.target.value = maskedValue;
  };

  const handleDateChange = (event: any) => {
    const maskedValue = dateMask(event.target.value);
    event.target.value = maskedValue;
  };

  const accountExistsCode = formError?._account_exists;
  const accountExistsAlertRef = useRef<HTMLDivElement>(null);
  const fieldServerError = (() => {
    if (!formError) return null;
    const { _account_exists: _marker, ...fieldErrors } = formError;
    return Object.keys(fieldErrors).length > 0 ? fieldErrors : null;
  })();

  useEffect(() => {
    if (!accountExistsCode || !accountExistsAlertRef.current) return;
    accountExistsAlertRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [accountExistsCode]);

  return (
    <>
      {accountExistsCode && (
        <div
          ref={accountExistsAlertRef}
          className="relative mb-6 rounded border border-accent/30 bg-accent/5 p-4 pr-10 text-sm"
          role="alert"
        >
          <button
            type="button"
            aria-label="Close"
            title="Close alert"
            onClick={() => setFormError(null)}
            className="absolute top-3 right-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-accent transition-colors duration-200 hover:bg-gray-300 hover:bg-opacity-25 focus:bg-gray-300 focus:bg-opacity-25 focus:outline-0"
          >
            <span aria-hidden="true">
              <CloseIcon className="h-5 w-5" />
            </span>
          </button>
          <p className="font-semibold text-body-dark">
            {ptBrCommon('register-email-exists-title')}
          </p>
          <p className="mt-1 text-body">
            {ptBrCommon('register-email-exists-detail')}
          </p>
          <div className="mt-3 flex gap-3">
            <button
              type="button"
              onClick={() => openModal('LOGIN_VIEW')}
              className="text-accent underline hover:no-underline"
            >
              {ptBrCommon('go-to-login')}
            </button>
            <span className="text-muted-400">·</span>
            <button
              type="button"
              onClick={() => openModal('FORGOT_VIEW')}
              className="text-accent underline hover:no-underline"
            >
              {ptBrCommon('go-to-forgot-password')}
            </button>
          </div>
        </div>
      )}
      <Form<RegisterUserInput>
        onSubmit={onSubmit}
        validationSchema={registerFormSchema}
        serverError={fieldServerError}
      >
        {({ register, formState: { errors, isDirty }, watch, setValue }) => {
          const passwordValue = watch('password');
          const cellphoneValue = watch('cellphone');
          const phoneValue = watch('phone');

          return (
            <>
              <div className="grid gap-y-2 md:grid-cols-2 md:gap-x-4">
                <Input
                  label={"Nome"}
                  {...register('first_name')}
                  variant="outline"
                  className="mb-5"
                  inputClassName="bg-blue-100"
                  error={ptBrCommon(errors.first_name?.message!)}
                  required
                />
                <Input
                  label={"Sobrenome"}
                  {...register('last_name')}
                  variant="outline"
                  className="mb-5"
                  inputClassName="bg-blue-100"
                  error={ptBrCommon(errors.last_name?.message!)}
                  required
                />

                <div className="mb-5" id="cellphone">
                  <label
                    htmlFor="cellphone"
                    className="ml-4 mb-3 block text-sm font-semibold leading-none text-accent"
                  >
                    {"Celular com DDD"} *
                  </label>

                  <PhoneInput
                    country="br"
                    onlyCountries={['br']}
                    disableDropdown
                    placeholder="+55 (99) 99999-9999"
                    onChange={(phone) => {
                      setValue('cellphone', phone);
                    }}
                    value={cellphoneValue}
                    inputProps={{
                      id: 'cellphone-input',
                      name: 'cellphone',
                    }}
                    inputClass="!p-0 ltr:!pr-4 rtl:!pl-4 ltr:!pl-4 rtl:!pr-14 !flex !items-center !w-full !appearance-none !transition !duration-300 !ease-in-out !text-gray-200 !text-sm !bg-blue-100 focus:!outline-none focus:!ring-0 !border !border-border-base !rounded-full focus:!border-accent !h-12"
                    containerClass="phone-input-container"
                  />
                  {errors.cellphone?.message && (
                    <p className="my-2 text-xs text-red-500 ltr:text-left rtl:text-right">
                      {ptBrCommon(errors.cellphone?.message)}
                    </p>
                  )}
                </div>

                <div className="mb-5" id="phone">
                  <label
                    htmlFor="phone"
                    className="ml-4 mb-3 block text-sm font-semibold leading-none text-accent"
                  >
                    {"Telefone com DDD"}
                  </label>

                  <PhoneInput
                    country="br"
                    onlyCountries={['br']}
                    disableDropdown
                    placeholder="+55 (99) 99999-9999"
                    value={phoneValue}
                    onChange={(phone) => {
                      setValue('phone', phone);
                    }}
                    inputProps={{
                      id: 'phone-input',
                      name: 'phone',
                    }}
                    inputClass="!p-0 ltr:!pr-4 rtl:!pl-4 ltr:!pl-4 rtl:!pr-14 !flex !items-center !w-full !appearance-none !transition !duration-300 !ease-in-out !text-gray-200 !text-sm !bg-blue-100 focus:!outline-none focus:!ring-0 !border !border-border-base !rounded-full focus:!border-accent !h-12"
                    containerClass="phone-input-container"
                  />
                </div>

                <div className="col-span-full mb-5">
                  <button
                    type="button"
                    onClick={toggleInput}
                    className="cursor-pointer flex items-center text-accent mb-2"
                  >
                    {isInputVisible ? (
                      <MinusIcon className="h-5 w-5 mr-2" />
                    ) : (
                      <PlusIcon className="h-5 w-5 mr-2" />
                    )}
                    Deseja adicionar nome social?
                  </button>
                  <div
                    className={`transition-all duration-300 ease-in-out overflow-hidden ${
                      isInputVisible ? 'max-h-20' : 'max-h-0'
                    }`}
                  >
                    <Input
                      label={"Nome social"}
                      {...register('social_name')}
                      variant="outline"
                      className="mb-5"
                      inputClassName="bg-blue-100"
                      error={ptBrCommon(errors.social_name?.message!)}
                      required
                    />
                  </div>
                </div>

                <Input
                  label={"CPF"}
                  {...register('cpf', {
                    onChange: handleCPFChange,
                  })}
                  variant="outline"
                  className="mb-5"
                  inputClassName="bg-blue-100"
                  error={ptBrCommon(errors.cpf?.message!)}
                  required
                />
                <Input
                  label={"E-mail"}
                  {...register('email')}
                  type="email"
                  variant="outline"
                  className="mb-5"
                  inputClassName="bg-blue-100"
                  error={ptBrCommon(errors.email?.message!)}
                  required
                />
                <PasswordInput
                  label={"Senha"}
                  {...register('password')}
                  error={ptBrCommon(errors.password?.message!)}
                  variant="outline"
                  className="mb-5"
                  inputClassName="bg-blue-100"
                  required
                />
                <PasswordInput
                  label={"Repetir senha"}
                  {...register('repeat_password')}
                  error={ptBrCommon(errors.repeat_password?.message!)}
                  variant="outline"
                  className="mb-5"
                  inputClassName="bg-blue-100"
                  required
                />

                <div className="col-span-full mb-5">
                  <PasswordRules password={passwordValue} />
                </div>

                <div className="col-span-full mb-5" id="gender">
                  <legend className="font-bold text-gray-200 mb-4">
                    Com qual gênero você se identifica? *
                  </legend>
                  <div className="flex flex-row items-start gap-x-4">
                    <Radio
                      {...register('gender')}
                      id="masculino"
                      label="Masculino"
                      value="male"
                    />
                    <Radio
                      {...register('gender')}
                      id="feminino"
                      label="Feminino"
                      value="female"
                    />
                    <Radio
                      {...register('gender')}
                      id="outros"
                      label="Outros"
                      value="others"
                    />
                  </div>
                  {errors.gender?.message && (
                    <p className="my-2 text-xs text-red-500 ltr:text-left rtl:text-right">
                      {ptBrCommon(errors.gender?.message)}
                    </p>
                  )}
                </div>

                <Input
                  label={"Data de nascimento"}
                  placeholder="dd/mm/yyyy"
                  {...register('birth_date', {
                    onChange: handleDateChange,
                  })}
                  variant="outline"
                  className="mb-5 col-span-full"
                  inputClassName="bg-blue-100"
                  error={ptBrCommon(errors.birth_date?.message!)}
                  required
                />

                <Checkbox
                  {...register('receive_email')}
                  label="Desejo Receber E-mail"
                  error={ptBrCommon(errors.receive_email?.message!)}
                  className="mb-5 col-span-full"
                />

                <Checkbox
                  {...register('accepted_terms')}
                  label={
                    <span>
                      Concordo com os{' '}
                      <Link
                        href="/institucional#termos"
                        className="mx-1 cursor-pointer text-accent underline hover:no-underline"
                        onClick={closeModal}
                      >
                        Termos e condições de uso.
                      </Link>
                    </span>
                  }
                  error={ptBrCommon(errors.accepted_terms?.message!)}
                />
              </div>
              <TurnstileWidget
                key={turnstileKey}
                action={TURNSTILE_ACTION}
                onVerify={setTurnstileToken}
              />
              {TURNSTILE_ENABLED && missingChallenge ? (
                <p className="mt-2 text-xs text-red-500">
                  Complete o desafio de segurança para continuar.
                </p>
              ) : null}
              <div className="mt-8">
                <Button
                  type="submit"
                  className="h-12 w-full"
                  loading={isLoading}
                  disabled={isLoading || !isDirty || missingChallenge}
                >
                  {"Cadastrar"}
                </Button>
              </div>
            </>
          );
        }}
      </Form>
      {/* End of forgot register form */}

      <div className="relative mt-8 mb-6 flex flex-col items-center justify-center text-sm text-heading sm:mt-11 sm:mb-8">
        <hr className="w-full" />
        <span className="absolute -top-2.5 bg-light px-2 ltr:left-2/4 ltr:-ml-4 rtl:right-2/4 rtl:-mr-4">
          {"OU"}
        </span>
      </div>
      <div className="text-center text-sm text-body sm:text-base">
        {"Já tem uma conta?"}{' '}
        <button type="button"
          onClick={() => openModal('LOGIN_VIEW')}
          className="font-semibold text-accent underline transition-colors duration-200 hover:text-accent-hover hover:no-underline focus:text-accent-hover focus:no-underline focus:outline-0 ltr:ml-1 rtl:mr-1"
        >
          {"Entrar"}
        </button>
      </div>
    </>
  );
}
export default function RegisterView() {
  return (
    <div className="flex h-full min-h-screen w-screen flex-col justify-center bg-light py-6 px-5 sm:p-8 md:h-auto md:min-h-0 md:max-w-[780px] md:rounded-xl">
      <div className="flex justify-start mb-8">
        <h2 className="text-accent font-semibold text-4xl">Bem-vindo(a)!</h2>
      </div>
      <RegisterForm />
    </div>
  );
}
